import { isAbortError, NetworkError } from '../api/error.js';

export interface BlobUrlHandle {
  readonly blobUrl: string;
  readonly sizeBytes: number;
  readonly contentType: string;
  revoke(): void;
}

export interface LoadProgress {
  readonly loaded: number;
  // 0 if Content-Length is missing/invalid (indeterminate progress).
  readonly total: number;
}

export interface LoadModelOptions {
  signal?: AbortSignal;
  onProgress?: (p: LoadProgress) => void;
  fetch?: typeof globalThis.fetch;
}

const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

function parseContentLength(header: string | null): number {
  if (header === null) return 0;
  const n = Number.parseInt(header, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Loader downloads the splat model via CORS, streams progress, and exposes
// it to the viewer as a blob: URL. The blob URL bypasses COEP `require-corp`
// because it is same-origin from the browser perspective, even though the
// underlying R2 object is cross-origin (see SPIKE_4C0_REPORT.md sezione 6).
//
// Memory note: chunks are accumulated in memory before Blob construction.
// For 50-200 MB .ply files this peaks at ~2x model size (chunks + Blob)
// then settles at ~1x once chunks go out of scope. Using `response.blob()`
// directly would be more memory-efficient but loses fine-grained progress.
export async function loadModelAsBlobUrl(
  downloadUrl: string,
  opts: LoadModelOptions = {},
): Promise<BlobUrlHandle> {
  const fetchFn = opts.fetch ?? globalThis.fetch.bind(globalThis);

  let response: Response;
  try {
    response = await fetchFn(downloadUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      ...(opts.signal !== undefined && { signal: opts.signal }),
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new NetworkError(err instanceof Error ? err.message : String(err), err);
  }

  if (!response.ok) {
    throw new NetworkError(`Model download failed: HTTP ${response.status}`);
  }

  const total = parseContentLength(response.headers.get('Content-Length'));
  const contentType = response.headers.get('Content-Type') ?? DEFAULT_CONTENT_TYPE;
  const body = response.body;

  const chunks: Uint8Array[] = [];
  let loaded = 0;

  if (body === null) {
    // Browsers without streaming body (legacy) — fall back to .blob(). No progress.
    if (opts.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    const blob = await response.blob();
    if (opts.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    const blobUrl = URL.createObjectURL(blob);
    if (opts.onProgress !== undefined) {
      opts.onProgress({ loaded: blob.size, total: blob.size });
    }
    return {
      blobUrl,
      sizeBytes: blob.size,
      contentType,
      revoke: () => URL.revokeObjectURL(blobUrl),
    };
  }

  const reader = body.getReader();
  // If the signal aborts while reader.read() is pending (no chunks coming),
  // cancel the reader so read() rejects and the loop unwinds.
  const onAbort = (): void => {
    void reader.cancel(opts.signal?.reason ?? new DOMException('Aborted', 'AbortError'));
  };
  if (opts.signal !== undefined) {
    if (opts.signal.aborted) {
      onAbort();
    } else {
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }
  }
  try {
    for (;;) {
      if (opts.signal?.aborted) {
        throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
      }
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (err) {
        if (opts.signal?.aborted) {
          throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
        }
        throw err;
      }
      if (chunk.done) break;
      if (chunk.value !== undefined) {
        chunks.push(chunk.value);
        loaded += chunk.value.byteLength;
        if (opts.onProgress !== undefined) {
          opts.onProgress({ loaded, total });
        }
      }
    }
  } finally {
    if (opts.signal !== undefined) {
      opts.signal.removeEventListener('abort', onAbort);
    }
    try {
      reader.releaseLock();
    } catch {
      // releaseLock may throw if reader already cancelled; ignore.
    }
  }

  // Final abort check: reader.cancel() may have closed the stream cleanly
  // before the loop noticed signal.aborted. Don't return a partial blob.
  if (opts.signal?.aborted) {
    throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
  }

  const blob = new Blob(chunks as BlobPart[], { type: contentType });
  // Release chunk refs ASAP so the Blob is the only retainer.
  chunks.length = 0;
  const blobUrl = URL.createObjectURL(blob);

  return {
    blobUrl,
    sizeBytes: blob.size,
    contentType,
    revoke: () => URL.revokeObjectURL(blobUrl),
  };
}
